import React, { useState, useEffect } from 'react';
import './index.scss';
import { handleLinkClick } from '@/Utils/routing';
import { useUniversity } from '@/Recoil/Admission/University';
import LanguageSelector from '@/Components/LanguageSelector';
import { createI18n } from '@/Utils/i18n';
import { languageManager } from '@/Utils/languageManager';
import { StudyAbroadPaymentWall } from '@/Components/PaymentWall';
import { usePurchaseHistory } from '@/Recoil/Payment/Purchase';

interface UniversityProps {
    universityId: string;
}


const translations = {
    'Loading university data...': { id: 'Memuat data universitas...', ko: '대학 데이터 로딩 중...' },
    'Check Eligibility': { id: 'Periksa Kelayakan Pendaftaran', ko: '지원 자격 확인' },
    'School Advantages and Features': { id: 'Keunggulan dan Fitur Sekolah', ko: '학교 장점 및 특징' },
    'University introduction not available': { id: 'Pengenalan universitas tidak tersedia', ko: '학교 소개를 사용할 수 없습니다' },
    'School Information': { id: 'Informasi Sekolah', ko: '학교 정보' },
    'School information not available': { id: 'Informasi sekolah tidak tersedia', ko: '학교 정보를 사용할 수 없습니다' },
    'Eligibility Requirements': { id: 'Persyaratan Kelayakan', ko: '지원 자격' },
    'Eligibility Requirements (LIC)': { id: 'Persyaratan Kelayakan (LIC)', ko: '지원 자격 (LIC)' },
    'Track eligibility data not available': { id: 'Data kelayakan track tidak tersedia', ko: '트랙 자격 데이터를 사용할 수 없습니다' },
    'Admission Guide': { id: 'Panduan Penerimaan', ko: '전형 안내' },
    'Admission Guide (LIC)': { id: 'Panduan Penerimaan (LIC)', ko: '전형 안내 (LIC)' },
    'Application Deadline': { id: 'Batas Waktu Pendaftaran', ko: '지원 마감일' },
    'Announcement Date': { id: 'Tanggal Pengumuman', ko: '발표일' },
    'Selection Method': { id: 'Metode Seleksi', ko: '전형 방법' },
    'Documents 100%': { id: 'Dokumen 100%', ko: '서류 100%' },
    'Recruitment Capacity': { id: 'Kapasitas Penerimaan', ko: '모집 인원' },
    'people': { id: ' orang', ko: '명' },
    'Tuition Fee': { id: 'Biaya Kuliah', ko: '등록금' },
    'KRW': { id: ' KRW', ko: '원' },
    'Track application data not available': { id: 'Data aplikasi track tidak tersedia', ko: '트랙 지원 데이터를 사용할 수 없습니다' },
    'Majors': { id: 'Jurusan', ko: '전공' },
    'Track majors data not available': { id: 'Data jurusan track tidak tersedia', ko: '트랙 전공 데이터를 사용할 수 없습니다' },
    'Check Checklist': { id: 'Periksa Daftar Periksa', ko: '체크리스트 확인하기' },
    'Loading...': { id: 'Memuat...', ko: '로딩 중...' },
    'Track': { id: 'Track', ko: '트랙' }
};

const t = createI18n(translations);

const University: React.FC<UniversityProps> = ({ universityId }) => {
    const { universities, tracks, fetchUniversities, fetchTracks, loading } = useUniversity();
    const { hasProduct, error: purchaseError, loading: purchaseLoading } = usePurchaseHistory();
    // Treat fetch errors or loading as no purchase for safety
    const hasPurchased = !purchaseError && !purchaseLoading && hasProduct('mate_study_abroad.250917.01');
    // Force re-render when language changes
    const [, forceUpdate] = useState({});
    useEffect(() => {
        const unsubscribe = languageManager.subscribe(() => forceUpdate({}));
        return unsubscribe;
    }, []);
    const [activeTrack, setActiveTrack] = useState<string>('korean');
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const tabsRef = React.useRef<HTMLDivElement>(null);

    // Fetch data on component mount
    useEffect(() => {
        fetchUniversities({ universityid: universityId });
        fetchTracks({ universityid: universityId });
    }, [fetchUniversities, fetchTracks, universityId]);

    // Get current university and tracks data
    const currentUniversity = universities[universityId];
    const universityTracks = Object.values(tracks).filter(track => track.universityid === universityId);
    
    // Get current track
    const currentTrack = universityTracks.find(track => track.trackid === activeTrack) || universityTracks[0];
    
    // Set default active track when tracks are loaded
    useEffect(() => {
        if (universityTracks.length > 0 && !universityTracks.find(t => t.trackid === activeTrack)) {
            setActiveTrack(universityTracks[0].trackid);
        }
    }, [universityTracks, activeTrack]);

    if (loading) {
        return (
            <div className="UnivIntro">
                <div className="Hero"></div>
                <div className="Overlay">
                    <div className="Logo"></div>
                    <div className="Texts">
                        <p>{t('Loading university data...')}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={`UnivIntro ${!hasPurchased ? 'payment-restricted' : ''}`}>
            <StudyAbroadPaymentWall productid="mate_study_abroad.250917.01" forceShow />
            {/* Top full-bleed image */}
            <div className="Hero" style={{ backgroundImage: `url('${currentUniversity?.background_url || '/img/default-university-bg.png'}')` }}>
                {/* Language Selector */}
                <div style={{ position: 'absolute', top: 20, right: 20 }}>
                    <LanguageSelector onChange={(newLang) => {
                        languageManager.setLanguage(newLang as any);
                        // Re-fetch data with new language header
                        fetchUniversities({ universityid: universityId });
                        fetchTracks({ universityid: universityId });
                    }} />
                </div>
            </div>

            {/* Overlay Info block */}
            <div className="Overlay">
                <div className="Logo" style={{ backgroundImage: `url('${currentUniversity?.logo_url || '/img/default-university-logo.png'}')` }}></div>
                <div className="Texts">
                    <div className="TitleRow">
                        <p className="Title">{currentUniversity?.name || t('Loading...')}</p>
                        <div className="Badges">
                            {currentUniversity?.badges?.map((badge, index) => (
                                <div key={index} className={`Badge ${badge.type}`}>
                                    {badge.text}
                                </div>
                            )) || []}
                        </div>
                    </div>
                    <p className="Address">{currentUniversity?.address || currentUniversity?.location || t('Loading...')}</p>
                </div>
            </div>

            {/* Track Tabs */}
            <div className="Tabs">
                <div className="Container" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <div 
                        ref={tabsRef}
                        className="TabsScrollContainer"
                        style={{ 
                            flex: 1, 
                            overflow: 'auto', 
                            display: 'flex',
                            cursor: isDragging ? 'grabbing' : 'grab',
                            userSelect: 'none'
                        }}
                        onMouseDown={(e) => {
                            setIsDragging(true);
                            setStartX(e.pageX - (tabsRef.current?.offsetLeft || 0));
                            setScrollLeft(tabsRef.current?.scrollLeft || 0);
                        }}
                        onMouseLeave={() => setIsDragging(false)}
                        onMouseUp={() => setIsDragging(false)}
                        onMouseMove={(e) => {
                            if (!isDragging || !tabsRef.current) return;
                            e.preventDefault();
                            const x = e.pageX - (tabsRef.current.offsetLeft || 0);
                            const walk = (x - startX) * 2;
                            tabsRef.current.scrollLeft = scrollLeft - walk;
                        }}
                    >
                        <div className="Left" style={{ display: 'flex', gap: '8px', whiteSpace: 'nowrap' }}>
                            {universityTracks.map((track) => (
                                <div 
                                    key={track.trackid}
                                    className={`Tab ${activeTrack === track.trackid ? 'active' : ''}`}
                                    onClick={() => {
                                        if (!isDragging) {
                                            setActiveTrack(track.trackid);
                                        }
                                    }}
                                    style={{ flexShrink: 0 }}
                                >
                                    {track.name}
                                </div>
                            ))}
                        </div>
                    </div>
                    <a 
                        className="Action" 
                        type="button" 
                        onClick={handleLinkClick} 
                        href={`/admission/${universityId}/${activeTrack}`}
                        style={{ flexShrink: 0 }}
                    >
                        {t('Check Eligibility')}
                    </a>
                </div>
            </div>

            {/* Shared Content - Always visible */}
            <div className="Content Active">
                <div className="Section">
                    <p className="Title Light">{t('School Advantages and Features')}</p>
                    <div className="Card">
                        <p>{currentUniversity?.introduction || t('University introduction not available')}</p>
                    </div>
                </div>

                <div className="Divider Divider spaced"></div>

                {/* Shared School Info - Always visible */}
                <div className="Info">
                    <p className="Title">{t('School Information')}</p>
                    <div className="Items">
                        {currentUniversity?.school_info?.map((info, index) => (
                            <div key={index} className="Item">
                                <div className={`Icon ${info.icon}`}></div>
                                <p className="Label">{info.label}</p>
                                <p className="Value">{info.value}</p>
                            </div>
                        )) || [
                            <div key="no-data" className="Item">
                                <p className="Label">{t('School information not available')}</p>
                            </div>
                        ]}
                    </div>
                </div>

                {/* Tab-specific content */}
                <div className="Section">
                    <p className="Title">{activeTrack === 'lic' ? t('Eligibility Requirements (LIC)') : t('Eligibility Requirements')}</p>
                    <div className="Table">
                        {currentTrack?.eligibility?.map((item, index) => (
                            <React.Fragment key={index}>
                                <div className="TableRow">
                                    <div className="TableCell Label">
                                        <p>{item.label}</p>
                                    </div>
                                    <div className="TableCell Value">
                                        <p>{item.value}</p>
                                    </div>
                                </div>
                            </React.Fragment>
                        )) || [
                            <div key="no-data" className="TableRow">
                                <p>{t('Track eligibility data not available')}</p>
                            </div>
                        ]}
                    </div>
                </div>

                <div className="Section">
                    <p className="Title">{activeTrack === 'lic' ? t('Admission Guide (LIC)') : t('Admission Guide')}</p>
                    <div className="Table">
                        {currentTrack ? [
                            { label: t('Application Deadline'), value: new Date(currentTrack.application_deadline * 1000).toLocaleDateString() },
                            { label: t('Announcement Date'), value: new Date(currentTrack.announcement_date * 1000).toLocaleDateString() },
                            { label: t('Selection Method'), value: currentTrack.description || t('Documents 100%') },
                            ...(currentTrack.capacity ? [{ label: t('Recruitment Capacity'), value: `${currentTrack.capacity}${t('people')}` }] : []),
                            ...(currentTrack.tuition_fee ? [{ label: t('Tuition Fee'), value: `${currentTrack.tuition_fee.toLocaleString()}${t('KRW')}` }] : [])
                        ].map((item, index) => (
                            <React.Fragment key={index}>
                                <div className="TableRow">
                                    <div className="TableCell Label">
                                        <p>{item.label}</p>
                                    </div>
                                    <div className="TableCell Value">
                                        <p>{item.value}</p>
                                    </div>
                                </div>
                            </React.Fragment>
                        )) : [
                            <div key="no-data" className="TableRow">
                                <p>{t('Track application data not available')}</p>
                            </div>
                        ]}
                    </div>
                </div>

                <div className="Divider Divider spaced"></div>

                <div className="Majors">
                    <div className="Header">
                        <p className="Title">{t('Majors')}</p>
                    </div>
                    <div className="Table">
                        {currentTrack?.majors?.map((major, majorIndex) => (
                            <React.Fragment key={majorIndex}>
                                <div className="TableRow">
                                    <div className="TableCell Label">
                                        <p>{major.category}</p>
                                    </div>
                                    <div className="TableCell Value">
                                        <div className="ChipList">
                                            {major.subjects.map((subject, subjectIndex) => (
                                                <span key={subjectIndex} className="Chip">{subject}</span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </React.Fragment>
                        )) || [
                            <div key="no-data" className="TableRow">
                                <div className="TableCell">
                                    <p>{t('Track majors data not available')}</p>
                                </div>
                            </div>
                        ]}
                    </div>
                </div>

                <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', alignItems: 'center', gap: 12, paddingTop: 24, paddingBottom: 24}}>
                    {/* Dynamic official docs links from university data */}
                    {currentUniversity?.documents?.map((doc, index) => (
                        <a key={index} className="Outlink" href={doc.url} target="_blank" rel="noopener noreferrer">{doc.title}</a>
                    ))}
                    
                    <a className="Action" type="button" onClick={handleLinkClick} href={`/admission/${universityId}/${activeTrack}`}>{`${
                        currentTrack?.name || t('Track')
                        } ${t('Check Checklist')}`}</a>
                </div>
            </div>
        </div>
    );
};

export default University;